/**
 * Authentication Context for YUL ON TAP.
 * 
 * OS-Style Pattern: Single auth system for all verticals.
 * Industry = presentation layer (branding at login)
 * Workspace = operational environment (Restaurants / Hotels)
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Workspace route mapping
const WORKSPACE_ROUTES = {
  restaurant: '/restaurants',
  hotel: '/hotels',
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentWorkspace, setCurrentWorkspace] = useState(
    localStorage.getItem('yul_current_workspace') || null
  );
  // Industry portal the user entered through (hard filter for workspaces)
  const [industryPortal, setIndustryPortal] = useState(
    localStorage.getItem('yul_industry_portal') || null
  );

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  /**
   * Set the industry portal (called when user selects industry at login)
   * This acts as a HARD FILTER for workspace visibility
   */
  const setEntryPortal = (industry) => {
    setIndustryPortal(industry);
    if (industry) {
      localStorage.setItem('yul_industry_portal', industry);
    } else {
      localStorage.removeItem('yul_industry_portal');
    }
  };

  const login = async (email, password, rememberMe = false) => {
    const response = await authAPI.login(email, password, rememberMe);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    // Store remember_me preference for this session (cleared on logout)
    if (rememberMe) {
      localStorage.setItem('yul_remember_me', 'true');
    }
    setUser(userData);
    
    return userData;
  };

  /**
   * Login with SSO - used when user is authenticated via SSO token.
   * Sets the auth state directly without making an API call.
   * 
   * @param {Object} userData - User data from SSO validation
   * @param {string} sessionToken - Session token from SSO validation
   * @param {string} industry - Industry portal type
   */
  const loginWithSSO = (userData, sessionToken, industry) => {
    localStorage.setItem('token', sessionToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('yul_industry_portal', industry);
    localStorage.setItem('yul_current_workspace', 'restaurant');
    
    setUser(userData);
    setIndustryPortal(industry);
    setCurrentWorkspace('restaurant');
    
    return userData;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('yul_current_workspace');
    localStorage.removeItem('yul_remember_me');
    localStorage.removeItem('yul_industry_portal');  // Clear industry portal
    setUser(null);
    setCurrentWorkspace(null);
    setIndustryPortal(null);
  };

  /**
   * Select a workspace (does NOT log out the user)
   */
  const selectWorkspace = (workspace) => {
    setCurrentWorkspace(workspace);
    if (workspace) {
      localStorage.setItem('yul_current_workspace', workspace);
    } else {
      localStorage.removeItem('yul_current_workspace');
    }
  };

  /**
   * Get workspaces allowed by the current industry portal.
   * Industry portal acts as a HARD FILTER:
   * - Restaurant/Automotive/Healthcare portals → only 'restaurant' workspace
   * - Hotel portal → only 'hotel' workspace
   */
  const getPortalAllowedWorkspaces = () => {
    const portal = industryPortal || localStorage.getItem('yul_industry_portal');
    if (!portal) return null; // No filter if no portal set
    
    // Map industry portal to allowed workspaces
    if (portal === 'hotel') {
      return ['hotel'];
    }
    // Restaurant, Automotive, Healthcare all map to 'restaurant' workspace
    return ['restaurant'];
  };

  /**
   * Check if user has access to a specific workspace/vertical.
   * Respects BOTH user permissions AND industry portal filter.
   */
  const hasWorkspaceAccess = (workspace) => {
    if (!user) return false;
    
    // First check user's vertical_access
    const hasPermission = user.vertical_access?.includes(workspace) || user.role === 'super_admin';
    if (!hasPermission) return false;
    
    // Then check industry portal filter
    const portalAllowed = getPortalAllowedWorkspaces();
    if (portalAllowed && !portalAllowed.includes(workspace)) {
      return false; // Industry portal blocks this workspace
    }
    
    return true;
  };

  /**
   * Get the list of workspaces user has access to.
   * Filtered by BOTH user permissions AND industry portal.
   */
  const getAccessibleWorkspaces = () => {
    if (!user) return [];
    
    const userWorkspaces = user.vertical_access || [];
    const portalAllowed = getPortalAllowedWorkspaces();
    
    // If no portal filter, return all user workspaces
    if (!portalAllowed) return userWorkspaces;
    
    // Filter by portal-allowed workspaces
    return userWorkspaces.filter(ws => portalAllowed.includes(ws));
  };

  /**
   * Check if user has access to multiple workspaces (within current portal)
   */
  const hasMultipleWorkspaces = () => {
    const workspaces = getAccessibleWorkspaces();
    return workspaces.length > 1;
  };

  /**
   * Determine the correct route after login based on industry portal.
   * 
   * HARD RULE: Industry portal determines destination directly.
   * - Restaurant/Automotive/Healthcare → /restaurants (existing system)
   * - Hotel → /hotels
   * 
   * NO workspace selector screen - ever.
   * 
   * @param {string} portal - The industry portal selected at login (optional, uses stored value if not provided)
   */
  const getPostLoginRoute = (portal = null) => {
    if (!user) return '/login';
    
    // Get the effective portal (passed in, or from state/localStorage)
    const effectivePortal = portal || industryPortal || localStorage.getItem('yul_industry_portal');
    
    if (!effectivePortal) {
      // Fallback for legacy - should not happen with new login flow
      return '/select-vertical';
    }
    
    // HARD ROUTING based on industry portal
    if (effectivePortal === 'hotel') {
      localStorage.setItem('yul_current_workspace', 'hotel');
      return '/hotels';
    }
    
    // Restaurant, Automotive, Healthcare → all go to restaurants (existing system)
    localStorage.setItem('yul_current_workspace', 'restaurant');
    return '/restaurants';
  };

  /**
   * Get the route for a specific workspace
   */
  const getWorkspaceRoute = (workspace) => {
    return WORKSPACE_ROUTES[workspace] || '/select-vertical';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        currentWorkspace,
        industryPortal,
        login,
        loginWithSSO,
        register,
        logout,
        selectWorkspace,
        setEntryPortal,
        hasWorkspaceAccess,
        getAccessibleWorkspaces,
        hasMultipleWorkspaces,
        getPostLoginRoute,
        getWorkspaceRoute,
        isAuthenticated: !!user,
        // Legacy aliases for compatibility
        selectVertical: selectWorkspace,
        hasVerticalAccess: hasWorkspaceAccess,
        currentVertical: currentWorkspace,
        industryType: user?.industry_type || 'restaurant',
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
